import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// Utils
import { getTableContent } from '../../../../../../../common/elements/utils/elementPropertyUtils';
import { asObject, length, toArray } from '../../../../../../../common/utils/immutableHelper';
import { getSharedFormattingValues } from '../../../../../../element/table/legacy/utils/tableCellTypeUtils';
import { getAllCellsBetween } from '../../../../../../element/table/legacy/utils/tableCellSelectionUtils';

// Selectors
import { useLegacyActiveTableSelector } from '../../../../../../element/table/store/legacyHotTableStoreHooks';
import { selectLegacyActiveTableCellSelection } from '../../../../../../element/table/store/selectors/legacyActiveHotTableSelectors';
import { userLanguagePreferenceSelector } from '../../../../../../user/currentUserSelector';

// Components
import ToolbarPopupTool from '../../../ToolbarPopupTool';
import LegacyCellTypeToolbarPopup from './LegacyCellTypeToolbarPopup';
import Icon from '../../../../../../components/icons/Icon';

// Constants
import { PopupIds, PopupSections } from '../../../../../../components/popupPanel/popupConstants';
import { CellTypeConstants } from '../../../../../../../common/table/CellTypeConstants';
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { MNTableContent } from '../../../../../../../common/table/TableTypes';

type CellTypeToolIconProps = {
    activeCellType: keyof typeof CellTypeConstants;
};

const CellTypeToolIcon: React.FC<CellTypeToolIconProps> = (props) => {
    const { activeCellType } = props;

    const iconName = CellTypeConstants[activeCellType]?.icon || 'toolbar-cell-type';
    return <Icon name={iconName} />;
};

type LegacyCellTypeToolProps = {
    element: ImMNElement;
};

const LegacyCellTypeTool: React.FC<LegacyCellTypeToolProps> = (props) => {
    const { element } = props;

    const locale = useSelector(userLanguagePreferenceSelector);
    const currentCellSelections = useLegacyActiveTableSelector(selectLegacyActiveTableCellSelection);

    const allSelectedCells = getAllCellsBetween(toArray(currentCellSelections));
    const data = (asObject(getTableContent(element)) as MNTableContent)?.data;

    // formatting options that are shared between all the selected cells
    // these will be the preselected options in the popup
    const sharedFormattingValues = getSharedFormattingValues(data, allSelectedCells);
    const activeCellType = sharedFormattingValues?.name;

    // Keep track of the type that was clicked last so that we can show the formatting options
    const [lastClickedType, setLastClickedType] = useState(null);

    useEffect(() => {
        setLastClickedType(null);
    }, [currentCellSelections]);

    return (
        <ToolbarPopupTool
            {...props}
            // @ts-ignore - ToolbarPopupTool isn't TS so the type inference struggles
            locale={locale}
            name="Cell type"
            popupToolClassName="LegacyTableTool"
            popupId={PopupIds.CELL_TYPE}
            closeSection={PopupSections.TOOLBAR}
            active={false}
            IconComponent={CellTypeToolIcon}
            toolName="CellType"
            disabled={length(currentCellSelections) === 0}
            buttonSelector=".CelltypeTool .Icon"
            PopupComponent={LegacyCellTypeToolbarPopup}
            activeCellType={activeCellType}
            sharedFormattingValues={sharedFormattingValues}
            allSelectedCells={allSelectedCells}
            lastClickedType={lastClickedType}
            setLastClickedType={setLastClickedType}
            preventFocus
        />
    );
};

export default LegacyCellTypeTool;
